import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { examLibraryApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import useEnrolledExams from '../hooks/useEnrolledExams';
import { Button, Card, PageSkeleton, ErrorState, EmptyState } from '../components/ui';

function getExamId(exam) {
  return exam?.examId ?? exam?.exam_id ?? exam?.examID ?? exam?.exam?.examId ?? exam?.exam?.id ?? exam?.id;
}

export default function ExamLibraryPage() {
  const org = useOrg();
  const toast = useToast();
  const [tab, setTab] = useState('catalogue');
  const [view, setView] = useState('cards'); // 'cards' or 'table'
  const [catalogue, setCatalogue] = useState([]);
  const [catalogueLoading, setCatalogueLoading] = useState(true);
  const [catalogueError, setCatalogueError] = useState(null);
  const { exams: enrolled, loading: enrolledLoading, error: enrolledError, reload: reloadEnrolled } = useEnrolledExams();
  const [enrollingId, setEnrollingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    examLibraryApi.catalogue(org.id)
      .then((res) => { setCatalogue(res.data || []); setCatalogueError(null); })
      .catch((err) => setCatalogueError(apiErrorMessage(err, 'Failed to load exam catalogue')))
      .finally(() => setCatalogueLoading(false));
  }, [org.id]);

  const loading = catalogueLoading || enrolledLoading;
  const error = catalogueError || enrolledError;

  const enroll = async (exam) => {
    const examId = getExamId(exam);
    setEnrollingId(examId);
    try {
      await examLibraryApi.enroll(org.id, examId);
      toast.success(`${exam.title} added to your institute exam library.`);
      setTab('library');
      reloadEnrolled();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to enroll in exam'));
    } finally {
      setEnrollingId(null);
    }
  };

  const openExamDetails = (exam) => {
    const examId = getExamId(exam);
    navigate(`/exam-library/${examId}`);
  };

  const openQuestionBank = (exam) => {
    navigate(`/question-banks/${getExamId(exam)}`);
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  const enrolledExamIds = new Set(enrolled.map((e) => getExamId(e)));

  return (
    <>
      <div className="tabs">
        <button className={tab === 'catalogue' ? 'selected' : ''} onClick={() => setTab('catalogue')}>Prepgen.ai catalogue</button>
        <button className={tab === 'library' ? 'selected' : ''} onClick={() => setTab('library')}>Your library ({enrolled.length})</button>
        <span />
        <div className="view-toggle">
          <button className={`toggle-btn ${view === 'cards' ? 'active' : ''}`} onClick={() => setView('cards')} title="Card view">⊞</button>
          <button className={`toggle-btn ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')} title="List view">≡</button>
        </div>
      </div>
      {tab === 'catalogue' && (
        <>
          <div className="intro">
            <b>Prepgen.ai exam catalogue</b>
            <p>Enroll your institute in a Prepgen.ai exam to unlock its question pools, structure and notes for your students.</p>
          </div>
          {view === 'cards' ? (
            <div className="card-grid">
              {catalogue.length ? catalogue.map((exam) => (
                <Card key={exam.id} className="pack hoverable">
                  <div className="pack-top">
                    <span className="tag">{exam.category || 'Exam'}</span>
                    <span className="muted">{exam.subCategory || ''}</span>
                  </div>
                  <h2>{exam.title}</h2>
                  <p>{exam.description || 'Prepgen.ai managed exam structure.'}</p>
                  <footer>
                    {enrolledExamIds.has(getExamId(exam))
                      ? <Button variant="secondary" disabled>✓ Enrolled</Button>
                      : <Button disabled={enrollingId === getExamId(exam)} onClick={() => enroll(exam)}>
                          {enrollingId === getExamId(exam) ? 'Enrolling…' : 'Add to exam library'}
                        </Button>}
                  </footer>
                </Card>
              )) : <EmptyState title="Catalogue is empty">No exams are available in the Prepgen.ai catalogue yet. Check back soon.</EmptyState>}
            </div>
          ) : (
            <Card className="table-card exam-library-table">
              <table>
                <thead>
                  <tr>
                    <th>Exam</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th className="action-cell">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogue.length ? catalogue.map((exam) => {
                    const examId = getExamId(exam);
                    const enrolledAlready = enrolledExamIds.has(examId);
                    return (
                      <tr key={exam.id || examId}>
                        <td>
                          <span className="exam-title">{exam.title || 'Untitled exam'}</span>
                          <span className="exam-meta">{exam.description || 'Prepgen.ai managed exam structure.'}</span>
                        </td>
                        <td>
                          <span className="exam-pill">{exam.category || 'Exam'}</span>
                          {exam.subCategory ? <span className="exam-meta">{exam.subCategory}</span> : null}
                        </td>
                        <td>
                          <span className={`exam-pill ${enrolledAlready ? 'success' : ''}`}>
                            {enrolledAlready ? 'Enrolled' : 'Available'}
                          </span>
                        </td>
                        <td className="action-cell">
                          {enrolledAlready
                            ? <Button variant="secondary" disabled>✓ Enrolled</Button>
                            : <Button disabled={enrollingId === examId} onClick={() => enroll(exam)}>
                                {enrollingId === examId ? 'Enrolling…' : 'Add to exam library'}
                              </Button>}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="4">
                        <div className="table-empty">
                          <EmptyState title="Catalogue is empty">No exams are available in the Prepgen.ai catalogue yet. Check back soon.</EmptyState>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
      {tab === 'library' && (
        view === 'cards' ? (
          <div className="card-grid">
            {enrolled.length ? enrolled.map((item) => (
              <Card key={item.id} className="pack hoverable clickable" onClick={() => openExamDetails(item)}>
                <div className="pack-top">
                  <span className="tag">{item.shortName || 'Exam'}</span>
                  <span className="muted">Enrolled {item.enrolledAt ? new Date(item.enrolledAt).toLocaleDateString() : ''}</span>
                </div>
                <h2>{item.title}</h2>
                <p>Question pools and structure available for quizzes and the screener.</p>
                <footer className="pack-actions">
                  <Button variant="primary" onClick={(e) => { e.stopPropagation(); openExamDetails(item); }}>
                    Exam structure
                  </Button>
                  <Button variant="secondary" onClick={(e) => { e.stopPropagation(); openQuestionBank(item); }}>
                    Question bank
                  </Button>
                </footer>
              </Card>
            )) : (
              <EmptyState title="Your library is empty" action={<Button variant="secondary" onClick={() => setTab('catalogue')}>Browse the catalogue</Button>}>
                Enroll in an exam from the Prepgen.ai catalogue to unlock its question pools.
              </EmptyState>
            )}
          </div>
        ) : (
          <Card className="table-card exam-library-table">
            <table>
              <thead>
                <tr>
                  <th>Exam</th>
                  <th>Enrolled</th>
                  <th>Status</th>
                  <th className="action-cell">Action</th>
                </tr>
              </thead>
              <tbody>
                {enrolled.length ? enrolled.map((item) => (
                  <tr key={item.id || getExamId(item)} className="clickable" onClick={() => openExamDetails(item)}>
                    <td>
                      <span className="exam-title">{item.title || 'Untitled exam'}</span>
                      <span className="exam-meta">{item.shortName || 'Prepgen.ai managed exam'}</span>
                    </td>
                    <td>{item.enrolledAt ? new Date(item.enrolledAt).toLocaleDateString() : 'Recently added'}</td>
                    <td>
                      <span className="exam-pill success">Ready</span>
                    </td>
                    <td className="action-cell">
                      <div className="row-actions">
                        <Button variant="primary" onClick={(e) => { e.stopPropagation(); openExamDetails(item); }}>
                          Exam structure
                        </Button>
                        <Button variant="secondary" onClick={(e) => { e.stopPropagation(); openQuestionBank(item); }}>
                          Question bank
                        </Button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4">
                      <div className="table-empty">
                        <EmptyState title="Your library is empty" action={<Button variant="secondary" onClick={() => setTab('catalogue')}>Browse the catalogue</Button>}>
                          Enroll in an exam from the Prepgen.ai catalogue to unlock its question pools.
                        </EmptyState>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        )
      )}
    </>
  );
}
